import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Camera, MapPin, Edit2, LogOut, Plus, X, Loader2, Settings, Check, Share2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import BottomNav from '../components/BottomNav';
import ReferralSection from '../components/ReferralSection';
import VerificationSection from '../components/VerificationSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Profile() {
  const navigate = useNavigate();
  const { user, token, logout, updateUser } = useAuth();
  const fileInputRef = useRef(null);
  
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(user?.name || '');
  const [bio, setBio] = useState(user?.bio || '');
  const [age, setAge] = useState(user?.age || '');
  const [gender, setGender] = useState(user?.gender || '');
  const [lookingFor, setLookingFor] = useState(user?.looking_for || '');
  const [photos, setPhotos] = useState(user?.photos || []);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || photos.length >= 6) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const { data } = await axios.post(`${API}/profile/photo`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      const newPhotos = [...photos, data.photo_url];
      setPhotos(newPhotos);
      
      // Auto-save
      await axios.put(`${API}/profile`, { photos: newPhotos }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (e) {
      console.error('Failed to upload photo:', e);
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = async (index) => {
    const newPhotos = photos.filter((_, i) => i !== index);
    setPhotos(newPhotos);
    
    try {
      await axios.put(`${API}/profile`, { photos: newPhotos }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (e) {
      console.error('Failed to remove photo:', e);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/profile`, {
        name,
        bio,
        age: parseInt(age),
        gender,
        looking_for: lookingFor,
        photos
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      updateUser(data.user);
      setEditing(false);
    } catch (e) {
      console.error('Failed to save profile:', e);
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const mainPhoto = photos?.[0] || 'https://images.unsplash.com/photo-1694877164163-bd51561f794a?w=400';

  return (
    <div className="min-h-screen bg-[#050505] pb-24">
      {/* Header */}
      <header className="px-6 py-6 flex items-center justify-between">
        <h1 className="font-outfit text-2xl font-semibold text-white">Profile</h1>
        <div className="flex items-center gap-2">
          {editing ? (
            <Button
              data-testid="save-profile-btn"
              onClick={handleSave}
              disabled={saving}
              size="sm"
              className="bg-rose-600 hover:bg-rose-500"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              <span className="ml-1">Save</span>
            </Button>
          ) : (
            <Button
              data-testid="edit-profile-btn"
              onClick={() => setEditing(true)}
              variant="ghost"
              size="sm"
              className="text-zinc-400 hover:text-white"
            >
              <Edit2 className="w-4 h-4 mr-1" />
              Edit
            </Button>
          )}
          <Button
            data-testid="settings-btn"
            onClick={() => navigate('/app/settings')}
            variant="ghost"
            size="icon"
            className="text-zinc-400 hover:text-white"
          >
            <Settings className="w-5 h-5" />
          </Button>
          <Button
            data-testid="logout-btn"
            onClick={handleLogout}
            variant="ghost"
            size="icon"
            className="text-zinc-400 hover:text-white"
          >
            <LogOut className="w-5 h-5" />
          </Button>
        </div>
      </header>

      <main className="px-6">
        {/* Profile Photo */}
        <div className="relative w-32 h-32 mx-auto mb-6">
          <img
            src={mainPhoto}
            alt={user?.name}
            className="w-full h-full rounded-full object-cover border-4 border-zinc-800"
          />
          {editing && (
            <button
              data-testid="change-main-photo-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="absolute bottom-0 right-0 w-10 h-10 rounded-full bg-rose-600 flex items-center justify-center text-white shadow-lg"
            >
              {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
            </button>
          )}
        </div>

        {/* Name & Info */}
        {!editing ? (
          <div className="text-center mb-8">
            <h2 className="font-outfit text-2xl font-semibold text-white">
              {user?.name}, {user?.age}
            </h2>
            {user?.latitude && (
              <div className="flex items-center justify-center gap-1 text-zinc-500 mt-1">
                <MapPin className="w-4 h-4" />
                <span>Location enabled</span>
              </div>
            )}
            {user?.bio && (
              <p className="text-zinc-400 mt-3">{user?.bio}</p>
            )}
            
            {/* Payment Status */}
            <div className={`mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${user?.is_paid ? 'bg-green-500/20 text-green-400' : 'bg-zinc-800 text-zinc-400'}`}>
              {user?.is_paid ? (
                <>
                  <Check className="w-4 h-4" />
                  <span className="text-sm">Premium Member</span>
                </>
              ) : (
                <span className="text-sm">Free Account</span>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-5 mb-8">
            <div>
              <Label className="text-zinc-400">Name</Label>
              <Input
                data-testid="edit-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-2 bg-zinc-900 border-zinc-800 text-white"
              />
            </div>

            <div>
              <Label className="text-zinc-400">Age</Label>
              <Input
                data-testid="edit-age-input"
                type="number"
                value={age}
                onChange={(e) => setAge(e.target.value)}
                className="mt-2 bg-zinc-900 border-zinc-800 text-white"
              />
            </div>

            <div>
              <Label className="text-zinc-400">Gender</Label>
              <Select value={gender} onValueChange={setGender}>
                <SelectTrigger className="mt-2 bg-zinc-900 border-zinc-800 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="non-binary">Non-binary</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-zinc-400">Looking For</Label>
              <Select value={lookingFor} onValueChange={setLookingFor}>
                <SelectTrigger className="mt-2 bg-zinc-900 border-zinc-800 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="men">Men</SelectItem>
                  <SelectItem value="women">Women</SelectItem>
                  <SelectItem value="everyone">Everyone</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-zinc-400">Bio</Label>
              <Textarea
                data-testid="edit-bio-input"
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                rows={3}
                className="mt-2 bg-zinc-900 border-zinc-800 text-white resize-none"
              />
            </div>
          </div>
        )}

        {/* Photo Gallery */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-outfit font-medium text-white">Photos</h3>
            <span className="text-zinc-500 text-sm">{photos.length}/6</span>
          </div>
          
          <div className="grid grid-cols-3 gap-3">
            {photos.map((photo, index) => (
              <div key={index} className="relative aspect-square rounded-xl overflow-hidden">
                <img src={photo} alt="" className="w-full h-full object-cover" />
                {editing && (
                  <button
                    data-testid={`remove-photo-${index}`}
                    onClick={() => removePhoto(index)}
                    className="absolute top-2 right-2 w-6 h-6 rounded-full bg-black/60 flex items-center justify-center text-white"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
            {editing && photos.length < 6 && (
              <button
                data-testid="add-photo-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="aspect-square rounded-xl border-2 border-dashed border-zinc-700 flex flex-col items-center justify-center text-zinc-500 hover:border-rose-500 hover:text-rose-500"
              >
                {uploading ? (
                  <Loader2 className="w-6 h-6 animate-spin" />
                ) : (
                  <Plus className="w-6 h-6" />
                )}
              </button>
            )}
          </div>
        </section>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handlePhotoUpload}
          className="hidden"
        />

        {/* Referral Program */}
        <div className="mt-8">
          <ReferralSection />
        </div>

        {/* Profile Verification */}
        <div className="mt-6">
          <VerificationSection />
        </div>

        {/* Upgrade CTA for free users */}
        {!user?.is_paid && (
          <div className="mt-8 p-6 rounded-2xl bg-gradient-to-br from-rose-600/20 to-rose-600/5 border border-rose-500/20">
            <h3 className="font-outfit font-semibold text-white mb-2">Unlock All Features</h3>
            <p className="text-zinc-400 text-sm mb-4">Get unlimited swipes, chat, and video calls for one price.</p>
            <Button
              data-testid="upgrade-btn"
              onClick={() => navigate('/paywall')}
              className="w-full bg-rose-600 hover:bg-rose-500 text-white rounded-full"
            >
              Upgrade — $19.99
            </Button>
          </div>
        )}

        {/* Admin Marketing Toolkit */}
        {user?.is_admin && (
          <div className="mt-8 p-6 rounded-2xl bg-gradient-to-br from-zinc-800/50 to-zinc-900/50 border border-zinc-700/50">
            <h3 className="font-outfit font-semibold text-white mb-2 flex items-center gap-2">
              <Share2 className="w-5 h-5 text-rose-500" />
              Marketing Toolkit
            </h3>
            <p className="text-zinc-400 text-sm mb-4">Ready-to-post content for every free platform to grow your user base.</p>
            <Button
              data-testid="marketing-toolkit-btn"
              onClick={() => navigate('/app/marketing')}
              className="w-full bg-zinc-800 hover:bg-zinc-700 text-white rounded-full border border-zinc-600"
            >
              Open Marketing Toolkit
            </Button>
          </div>
        )}
      </main>

      <BottomNav active="profile" />
    </div>
  );
}
